<template>
  <div
    class="flex items-stretch xl:h-6 text-h4 lg:text-h3 xl:text-h2 font-bold w-full"
    :class="{ 'text-error-425 shake': error }"
  >
    <h1
      v-show="!editing"
      @click="isClamped ? (expanded = !expanded) : null"
      @keydown.space.prevent="isClamped ? (expanded = !expanded) : null"
      tabindex="0"
      :aria-expanded="expanded"
      class="line-clamp-3"
      ref="titleEl"
      :class="{
        'line-clamp-none': expanded,
        'pb-2': expanded,
        'cursor-pointer': isClamped,
      }"
    >
      <span v-if="modelValue">
        {{ modelValue }}
      </span>
      <span v-else>Saisir le nom du parapheur</span>
    </h1>
    <div v-show="editing" class="border-b-2">
      <input
        @blur="emitSubmit"
        ref="input"
        type="text"
        :value="modelValue"
        @input="emit('update:modelValue', getValue($event))"
        @keypress.enter="emitSubmit"
        :size="50"
        class="outline-offset-4 outline-primary-active bg-grey-950 rounded-t text-h6 lg:text-h4 px-1.5 py-1v w-full"
      />
    </div>
    <PButton
      v-if="!readonly && !showSaved"
      size="icon"
      mode="tertiary-borderless"
      @click="edit"
      class="ml-1"
      aria-label="Saisir le nom du parapheur"
    >
      <OhVueIcon :name="editIcon" :class="error ? 'text-error' : 'text-blue-france-sun-113'" scale="1.5" />
    </PButton>
    <SaveFieldStatus v-if="showSaved" :loading="asyncOperation.loading.value" scale="1.5" class="ml-2" load-size="md" />
  </div>
</template>

<script setup lang="ts">
import { type Nullable, useIcons, PButton, getValue } from "@pasta/box-v2";
import { OhVueIcon } from "oh-vue-icons";
import { RiEditFill } from "oh-vue-icons/icons";
import { computed, nextTick, ref } from "vue";
import { useAsyncOperation } from "../composable/useAsyncOperation";
import SaveFieldStatus from "./SaveFieldStatus.vue";
import { FormOperation } from "../types";

interface Props {
  modelValue: string;
  error?: boolean;
  readonly?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "update:modelValue", value: Nullable<string>): void;
  (e: "submit"): void;
}>();

const [editIcon] = useIcons(RiEditFill);
const input = ref<Nullable<HTMLInputElement>>(null);

const editing = ref<boolean>(false);
const expanded = ref<boolean>(false);
async function edit() {
  if (props.readonly) return;
  editing.value = true;
  await nextTick();
  input.value!.focus();
}

const titleEl = ref<HTMLDivElement>();

const isClamped = computed(() => {
  if (!titleEl.value) return false;

  const { scrollHeight, clientHeight } = titleEl.value;
  return scrollHeight - clientHeight > 5;
});

function emitSubmit() {
  if (editing.value) {
    emit("submit");
    editing.value = false;
  }
}

// save status
const asyncOperation = useAsyncOperation();
const showSaved = asyncOperation.getShowSaved(FormOperation.LETTER_FILE_NAME);

// https://stackoverflow.com/questions/8100770/auto-scaling-inputtype-text-to-width-of-value
// function resizeInput() {
//   input.value!.size = Math.min(props.modelValue.length || 5, 95);
// }
</script>

<style scoped>
.shake {
  animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  transform: translate3d(0, 0, 0);
}

@keyframes shake {
  10%,
  90% {
    transform: translate3d(-1px, 0, 0);
  }

  20%,
  80% {
    transform: translate3d(2px, 0, 0);
  }

  30%,
  50%,
  70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%,
  60% {
    transform: translate3d(4px, 0, 0);
  }
}
</style>
